import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/Button';
import Select from '../../components/Select';

interface Shift {
  id: string;
  code: string;
  name: string;
}
interface Employee {
  id: string;
  code: string;
  first_name: string;
  last_name: string;
}

export default function BranchSetup() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<'shifts' | 'employees'>('shifts');
  const [allShifts, setAllShifts] = useState<Shift[]>([]);
  const [assignedShiftIds, setAssignedShiftIds] = useState<string[]>([]);
  const [allocatedEmployees, setAllocatedEmployees] = useState<Employee[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [pickEmployeeId, setPickEmployeeId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [shiftsRes, branchShiftsRes, employeesRes, allocatedRes] = await Promise.all([
      api.get('/tms/shifts'),
      api.get(`/tms/branches/${id}/shifts`),
      api.get('/employees'),
      api.get(`/tms/branches/${id}/employees`),
    ]);
    setAllShifts(shiftsRes.data.shifts || []);
    setAssignedShiftIds((branchShiftsRes.data.shifts || []).map((s: any) => s.shift_id));
    setAllEmployees(employeesRes.data.employees || []);
    setAllocatedEmployees(allocatedRes.data.employees || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleShift = (shiftId: string) => {
    setAssignedShiftIds((prev) => (prev.includes(shiftId) ? prev.filter((s) => s !== shiftId) : [...prev, shiftId]));
  };

  const saveShifts = async () => {
    setSaving(true);
    try {
      await api.put(`/tms/branches/${id}/shifts`, { shiftIds: assignedShiftIds });
      alert('Shift assignment saved');
    } finally {
      setSaving(false);
    }
  };

  const addEmployee = async () => {
    if (!pickEmployeeId) return;
    await api.post(`/tms/branches/${id}/employees`, { employeeIds: [pickEmployeeId] });
    setPickEmployeeId('');
    load();
  };

  const removeEmployee = async (employeeId: string) => {
    await api.delete(`/tms/branches/${id}/employees/${employeeId}`);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Branch Setup</h1>
        <Link to="/tms/branches" className="text-blue-600 hover:underline text-sm">
          &larr; Back to Branches
        </Link>
      </div>

      <div className="flex gap-4 border-b mb-4">
        <button
          className={`pb-2 px-1 text-sm font-medium ${tab === 'shifts' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          onClick={() => setTab('shifts')}
        >
          Shift Assignment
        </button>
        <button
          className={`pb-2 px-1 text-sm font-medium ${tab === 'employees' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          onClick={() => setTab('employees')}
        >
          Employee Allocation
        </button>
      </div>

      {tab === 'shifts' && (
        <div>
          {allShifts.length === 0 && (
            <p className="text-sm text-gray-500 mb-4">No shifts configured yet — create one under Time Management → Shifts.</p>
          )}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {allShifts.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm border rounded p-2">
                <input type="checkbox" checked={assignedShiftIds.includes(s.id)} onChange={() => toggleShift(s.id)} />
                {s.name}
              </label>
            ))}
          </div>
          <Button onClick={saveShifts} loading={saving}>
            Save Shift Assignment
          </Button>
        </div>
      )}

      {tab === 'employees' && (
        <div>
          <div className="flex gap-3 mb-4 items-end">
            <div className="w-64">
              <Select
                label="Add Employee"
                options={allEmployees
                  .filter((e) => !allocatedEmployees.some((a) => a.id === e.id))
                  .map((e) => ({ value: e.id, label: `${e.code} — ${e.first_name} ${e.last_name}` }))}
                value={pickEmployeeId}
                onChange={(e) => setPickEmployeeId(e.target.value)}
              />
            </div>
            <Button onClick={addEmployee}>Allocate</Button>
          </div>
          <table className="min-w-full text-sm border rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Code</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocatedEmployees.map((e) => (
                <tr key={e.id} className="border-t">
                  <td className="p-2">{e.code}</td>
                  <td className="p-2">
                    {e.first_name} {e.last_name}
                  </td>
                  <td className="p-2">
                    <button className="text-red-600 hover:underline" onClick={() => removeEmployee(e.id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {allocatedEmployees.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">No employees allocated to this branch yet.</p>
          )}
        </div>
      )}
    </div>
  );
}
